import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const AutoCloseDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [showPaymentMethodId, setShowPaymentMethodId] = useState(false);
  const [orderBy, setOrderBy] = useState('orderNumber');
  const [order, setOrder] = useState('asc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter data based on column filters
  const filteredDetails = useMemo(() => {
    if (!details.length) return details;

    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;

    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amounts and order number
      if (['paymentAmount', 'tipAmount', 'totalAmount', 'orderNumber'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  // Determine if this is a store-level or company-level (all stores) drill-down
  const isStoreLevelDrillDown = businessDate && storeId;
  const isCompanyLevelDrillDown = businessDate && !storeId;

  useEffect(() => {
    if (open && businessDate) {
      fetchDetails();
    }
  }, [open, businessDate, storeId]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const token = await firebaseUser.getIdToken(true);

      // Build params - always use businessDate, optionally filter by storeId
      const params = {
        startDate: businessDate,
        endDate: businessDate,
      };
      if (storeId) {
        params.storeId = storeId;
      }

      const response = await axios.get('/hd-api/gl-export/auto-close-details', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setDetails(response.data.details || []);
    } catch (err) {
      console.error('Error fetching auto-close details:', err);
      setError('Failed to load auto-close payment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals from filtered data
  const totals = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => ({
        payment: acc.payment + (parseFloat(d.paymentAmount) || 0),
        tip: acc.tip + (parseFloat(d.tipAmount) || 0),
        total: acc.total + (parseFloat(d.totalAmount) || 0),
      }),
      { payment: 0, tip: 0, total: 0 }
    );
  }, [filteredDetails]);

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => {
      const row = {
        'Order #': item.orderNumber || '-',
      };
      if (isCompanyLevelDrillDown) {
        row['Store'] = item.storeId || '-';
      }
      row['Payment Method'] = item.paymentMethodName;
      row['Payment Method ID'] = item.paymentMethodId;
      row['Type'] = item.paymentType;
      row['Payment'] = parseFloat(item.paymentAmount) || 0;
      row['Tip'] = parseFloat(item.tipAmount) || 0;
      row['Total'] = parseFloat(item.totalAmount) || 0;
      row['Order GUID'] = item.orderGuid;
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Auto-Close');
    const filename = isStoreLevelDrillDown
      ? `auto-close-${storeId}-${businessDate}.xlsx`
      : `auto-close-all-stores-${businessDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Auto-Close Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {isStoreLevelDrillDown
              ? `670001 - Auto-Close - Store ${storeId} - ${businessDate}`
              : `670001 - Auto-Close - All Stores - ${businessDate}`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No auto-close payment details found for this entry.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'orderGuid'}
                          direction={orderBy === 'orderGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('orderGuid')}
                        >
                          Order GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderNumber'}
                        direction={orderBy === 'orderNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderNumber')}
                      >
                        Order #
                      </TableSortLabel>
                    </TableCell>
                    {isCompanyLevelDrillDown && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'storeId'}
                          direction={orderBy === 'storeId' ? order : 'asc'}
                          onClick={() => handleRequestSort('storeId')}
                        >
                          Store
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'paymentMethodName'}
                        direction={orderBy === 'paymentMethodName' ? order : 'asc'}
                        onClick={() => handleRequestSort('paymentMethodName')}
                      >
                        Payment Method
                      </TableSortLabel>
                    </TableCell>
                    {showPaymentMethodId && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'paymentMethodId'}
                          direction={orderBy === 'paymentMethodId' ? order : 'asc'}
                          onClick={() => handleRequestSort('paymentMethodId')}
                        >
                          Payment Method ID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'paymentType'}
                        direction={orderBy === 'paymentType' ? order : 'asc'}
                        onClick={() => handleRequestSort('paymentType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'paymentAmount'}
                        direction={orderBy === 'paymentAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('paymentAmount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'tipAmount'}
                        direction={orderBy === 'tipAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipAmount')}
                      >
                        Tip
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'totalAmount'}
                        direction={orderBy === 'totalAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalAmount')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['orderGuid'] || ''}
                          onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                            minWidth: 80,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['orderGuid'] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')} sx={{ p: 0.25 }}>
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderNumber'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderNumber'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('orderNumber')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    {isCompanyLevelDrillDown && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['storeId'] || ''}
                          onChange={(e) => handleColumnFilterChange('storeId', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                            minWidth: 60,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['storeId'] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('storeId')} sx={{ p: 0.25 }}>
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['paymentMethodName'] || ''}
                        onChange={(e) => handleColumnFilterChange('paymentMethodName', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['paymentMethodName'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('paymentMethodName')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    {showPaymentMethodId && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['paymentMethodId'] || ''}
                          onChange={(e) => handleColumnFilterChange('paymentMethodId', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                            minWidth: 80,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['paymentMethodId'] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('paymentMethodId')} sx={{ p: 0.25 }}>
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['paymentType'] || ''}
                        onChange={(e) => handleColumnFilterChange('paymentType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['paymentType'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('paymentType')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['paymentAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('paymentAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['paymentAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('paymentAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['tipAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('tipAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['tipAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('tipAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['totalAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('totalAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['totalAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('totalAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.orderGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showOrderGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.orderGuid || '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ReceiptIcon sx={{ fontSize: 16, color: 'action.active' }} />
                          {detail.orderNumber || '-'}
                        </Box>
                      </TableCell>
                      {isCompanyLevelDrillDown && (
                        <TableCell>{detail.storeId || '-'}</TableCell>
                      )}
                      <TableCell>{detail.paymentMethodName || '-'}</TableCell>
                      {showPaymentMethodId && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.paymentMethodId || '-'}
                        </TableCell>
                      )}
                      <TableCell>{detail.paymentType || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.paymentAmount)}</TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(detail.tipAmount) > 0 ? 'success.main' : 'text.secondary' }}>
                        {formatCurrency(detail.tipAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {formatCurrency(detail.totalAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} payment{filteredDetails.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showOrderGuid}
                      onChange={(e) => setShowOrderGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Order GUID</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showPaymentMethodId}
                      onChange={(e) => setShowPaymentMethodId(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Payment Method ID</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(totals.payment)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tips
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(totals.tip)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.total)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default AutoCloseDetailsDialog;
